/**
 * followupVisibility — when do mid-thread follow-up chips render?
 *
 * Owner directive 2026-08-11: recommendations are not an empty-thread-only
 * affair — EVERY thread offers personalized next-message chips. Owner
 * directive 2026-08-23 makes that literal: the row must appear "after a
 * message by Prest0n or others", uniformly, on every surface.
 *
 * One rule implements it: chips show when the latest real message in the
 * thread is someone ELSE's — never the viewer's own.
 *   • 1:1 thread — after every completed turn the assistant is last, so chips
 *     follow every reply (the "continue the conversation" experience).
 *   • Group thread — a teammate's message is also a fine moment to offer the
 *     viewer a next beat, so the row shows there too.
 *   • The viewer's own message last — hidden: it is being answered, and
 *     suggesting what to say next on top of your own message is noise.
 *
 * Pure module (mirrors shouldShowStarterChips) so the rule unit-tests without
 * React or the chat system.
 */

/** Sentinel senderId the shared store uses for assistant turns. */
const ASSISTANT_SENDER_ID = 'assistant';

/** Minimal message shape (avoids importing the full ChatMessage). */
export interface FollowupGateMessage {
  id?: string;
  isUser?: boolean;
  isSystem?: boolean;
  senderId?: string | null;
  sourceTag?: string;
  /** Interrupted/failed assistant turn (page-refresh loss, VM empty-answer card). */
  interrupted?: boolean;
  status?: string;
  /** The original user prompt preserved for one-click Regenerate. */
  regeneratePrompt?: string;
  text?: string;
}

/** The latest real (non-system) message, or null on an empty thread. */
export function lastNonSystemMessage(
  messages: ReadonlyArray<FollowupGateMessage> | undefined,
): FollowupGateMessage | null {
  if (!Array.isArray(messages)) return null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (m && m.isSystem !== true) return m;
  }
  return null;
}

/** Is this message a Prest0n turn? (Mirrors the send path's repliedToAssistant.) */
export function isAssistantMessage(message: FollowupGateMessage | null | undefined): boolean {
  if (!message) return false;
  return message.isUser !== true || message.senderId === ASSISTANT_SENDER_ID;
}

/**
 * A still-in-flight assistant SHAPE: the pending/streaming widgets ("thinking…",
 * "working · m:ss", tool-start cards) are plain assistant messages, so when the
 * per-thread busy bit is off (10-minute safety floor on long tasks, mid-task
 * clarifying finalize) they read as a completed answer to the gate — and their
 * per-second label repaints minted a NEW conversation tail each tick, each one
 * a fresh billed distillation (R17, 2026-08-16 Miles: "one ai call, not 4").
 * These are chrome, not conversation: they never open the gate and never enter
 * the tail.
 */
// 2026-09-02: the three-family regex below missed most of the in-flight
// shapes the client actually mints — showPending's DEFAULT 'pending' tag
// (services/globalTools/messaging.ts), tool "generating" placeholders
// (legal_doc_generating), mid-turn tool-result posts, and the attachment
// read-complete notice. Each unmatched shape read as a COMPLETED answer, so a
// tool turn opened the gate mid-flight and minted a second conversation tail:
// the first fetch took the 90s generation lease and the second — the real,
// final tail — got the busy branch's canned floor in ~300ms. That is the
// "canned chips first, real chips seconds later" swap, and its mount/unmount
// churn is the sub-second flicker. Widened here and pinned by a parity test
// against interruptedTurn's PENDING_SOURCE_TAGS so the two cannot drift again.
// Deliberately NOT any `_result` rule: a tool RESULT is the turn's answer, not
// chrome. `legal_doc_result` / `research_result` are obvious, but so is
// `prest0n_tool_result_<toolId>` — useGlobalTools' onToolResult posts the
// tool's finalized rendered HTML under that tag and sets `paintedThisTurn`,
// because a rendered tool result IS the turn's visible surface (browser tiles,
// widgets, a connect card). Matching it here made a tool-only turn — one whose
// entire answer is that surface, with no follow-up prose — look permanently
// in-flight, so the gate walked straight past it to the viewer's own message
// and chips never rendered at all (review finding, 2026-09-02). The mid-turn
// window it was meant to cover is the busy bit's job, and the hook now stands
// down entirely while its thread is mid-turn; only `prest0n_tool_start_` (the
// progress indicator) is chrome.
const PENDING_SOURCE_TAG = /(^pending$|_pending$|_generating$|_read_complete$|^prest0n_tool_start_|^prest0n_session_streaming$)/;

export function isPendingAssistantShape(
  message: FollowupGateMessage | null | undefined,
): boolean {
  if (!message || message.isUser === true) return false;
  if (message.status === 'pending' || message.status === 'streaming' || message.status === 'processing') {
    return true;
  }
  return typeof message.sourceTag === 'string' && PENDING_SOURCE_TAG.test(message.sourceTag);
}

/**
 * An assistant turn that FAILED to produce its answer (page-refresh
 * interruption, or the VM adapter's "finished without producing the answer"
 * card). Owner directive 2026-08-12 (malpractice-query screenshot): after an
 * errored turn the right recommendation is a RETRY of what the user was
 * going for — never freshly generated topic chips off a broken tail.
 */
export function isInterruptedAssistantMessage(
  message: FollowupGateMessage | null | undefined,
): boolean {
  if (!isAssistantMessage(message)) return false;
  return message?.interrupted === true || message?.status === 'interrupted';
}

export interface RetryFollowup {
  /** The exact prompt a retry chip should re-send (the lost user request). */
  regeneratePrompt: string;
  /**
   * sourceTag of the interrupted assistant message. Send-failure bubbles
   * (SEND_FAILURE_RETRY_SOURCE_TAG) resend WITHOUT re-appending the user
   * bubble — it already exists in the thread — exactly like the card's own
   * Retry button (2026-08-12 chip-parity fix).
   */
  sourceTag?: string;
}

/**
 * When the thread's latest real message is a FAILED assistant turn, resolve
 * the deterministic retry recommendation: the preserved regeneratePrompt,
 * falling back to the nearest preceding user message (the same fallback the
 * interrupted-card marker uses at mark time). Null when the thread is not in
 * an error state or no original prompt is recoverable.
 */
export function resolveRetryFollowup(
  messages: ReadonlyArray<FollowupGateMessage> | undefined,
): RetryFollowup | null {
  const last = lastNonSystemMessage(messages);
  if (!isInterruptedAssistantMessage(last)) return null;

  const preserved = (last?.regeneratePrompt || '').trim();
  if (preserved) return { regeneratePrompt: preserved, sourceTag: last?.sourceTag };

  if (Array.isArray(messages)) {
    for (let i = messages.length - 1; i >= 0; i -= 1) {
      const m = messages[i];
      if (m && m.isSystem !== true && m.isUser === true && m.senderId !== ASSISTANT_SENDER_ID) {
        const text = (m.text || '').trim();
        if (text) return { regeneratePrompt: text, sourceTag: last?.sourceTag };
        break;
      }
    }
  }
  return null;
}

/**
 * Is this message the VIEWER's own? Assistant turns never are. A human message
 * belongs to someone ELSE only when we can prove it: both sender ids present
 * and different. Without a resolved viewer id (1:1 threads, pre-P3 messages)
 * every human message reads as the viewer's own — the safe direction, since it
 * only ever keeps the row hidden.
 */
export function isViewersOwnMessage(
  message: FollowupGateMessage | null | undefined,
  selfId?: string | null,
): boolean {
  if (!message) return false;
  if (isAssistantMessage(message)) return false;
  const sender = (message.senderId || '').trim();
  const self = (selfId || '').trim();
  if (sender && self && sender !== self) return false;
  return true;
}

/** Is the thread still in its untouched welcome state? (Starter chips own it.) */
function isWelcomeOnlyThread(messages: ReadonlyArray<FollowupGateMessage>): boolean {
  const hasWelcome = messages.some((m) => m?.id === 'welcome' || m?.sourceTag === 'welcome');
  if (!hasWelcome) return false;
  return !messages.some((m) => m?.isUser === true && m?.isSystem !== true);
}

/**
 * Pure predicate: render (and fetch) follow-up chips whenever the latest real
 * CONVERSATION message is somebody ELSE's — Prest0n's, or another human's in a
 * group thread. Hidden only for the deterministic cases below.
 *
 * ALWAYS-ON (owner directive 2026-08-23: "the full-context suggested thread
 * messages … always need to display after a message by Prest0n or others").
 * Two clauses used to swallow the row in real threads and are now gone:
 *   • "the latest message must be Prest0n's" — in a group thread a teammate's
 *     message hid the row entirely. It only ever meant to suppress the row
 *     mid human↔human exchange, which is really about the VIEWER not being
 *     mid-request; a teammate's message is a fine moment to offer a next beat.
 *   • "the thread must contain a user message" — intended to keep empty
 *     threads for the starter row, but it also killed the row forever in the
 *     2026-08-20 split-brain repro (a reply rendered in a pane without its
 *     user bubble). The narrower welcome-state check below keeps the two rows
 *     mutually exclusive without that collateral.
 *
 * DEFAULT-ON (owner directive 2026-08-19, flicker report): chips follow every
 * landed assistant message — including progress/content messages posted MID
 * long run. The old `isProcessing` gate and the pending-widget-last gate made
 * the row flap for an hour during a deep-research run (busy bit heartbeats +
 * spinner churn opened and closed the gate over and over). The rule derives
 * ONLY from real conversation content: pending/streaming widget shapes are
 * chrome (extracted to the overlay lane at render), so the walk skips them
 * entirely — the row's state changes only when a real message lands.
 *
 * R17 billing invariant unchanged: pending shapes still never enter the tail,
 * so ticking labels still cannot mint per-tick distillations.
 */
export function shouldShowFollowupChips(params: {
  messages: ReadonlyArray<FollowupGateMessage> | undefined;
  /** Viewer's uid — distinguishes "I spoke last" from "a teammate spoke last". */
  selfId?: string | null;
}): boolean {
  const { messages, selfId } = params;
  if (!Array.isArray(messages) || messages.length === 0) return false;
  // Untouched welcome state belongs to the starter chips — the two rows never
  // show together.
  if (isWelcomeOnlyThread(messages)) return false;
  // Last REAL conversation message: skip system notes AND pending/streaming
  // chrome — a live spinner row must neither open nor close the gate.
  let last: FollowupGateMessage | null = null;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m || m.isSystem === true) continue;
    if (isPendingAssistantShape(m)) continue;
    last = m;
    break;
  }
  if (!last) return false;
  // Error state ⇒ NO generated chips (owner directive 2026-08-12): the thread
  // is mid-failure, so the deterministic retry recommendation
  // (resolveRetryFollowup) owns this moment — generating topic chips off a
  // broken tail is exactly the "less appropriate" recommendation reported.
  if (isInterruptedAssistantMessage(last)) return false;
  // The viewer's own message is last: it is being answered right now, and
  // suggesting what to say next on top of it is noise.
  return !isViewersOwnMessage(last, selfId);
}
